import { resolvePhoneNumberFormat, useAppContext, useFacilityContext, type IconName } from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import format from 'date-fns/format';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import AppointmentCard from './AppointmentCard';
import { GenerateReviewTile, generateReviewTileES } from './ReviewTileData';
import { REASONS_FOR_VISIT, REASONS_FOR_VISIT_ES, REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { APP_CONFIGURATIONS, DataLayerStepNames, PREFIX } from '@src/constants';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import { Application, PhoneType } from '@src/lib/__generated/api';
import { EventReviewAppointmentValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import { getLogger } from '@src/lib/logging/logger';
import { dataLayer } from '@src/services/dataLayer';
import { userEvent } from '@src/services/userEvent';
import { useScheduleAppointment } from '@src/services/useScheduleAppointment';
import { validateAccount } from '@src/services/useValidateAccount';
import { useAppStore, useAppointmentStore, useFormSectionStore, useGlobalUXStore, useGuarantorInformationFormStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
export type AppointmentInformation = {
  title: string;
  content: (string | null | undefined)[];
  icon: IconName;
  editSectionIndex?: number;
};
export type AppointmentSubmitHandle = {
  handleSubmit: () => void;
};
const AppointmentReview = forwardRef<AppointmentSubmitHandle>((_, ref) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    email,
    phonenumber,
    hasInsurance,
    digiact,
    setAccountNumber,
    setNewPatient,
    setHasScheduledAppointment,
    setDigiact,
    setChildPatient,
    geoLocation
  } = usePatientInformationFormStore(store => store);
  const {
    mottoFlow
  } = useAppStore(store => store);
  const {
    denturesFlow
  } = useAppStore(store => store);
  const {
    t
  } = useTranslation('appointmentReview');
  const {
    firstName: guarantorFirstName,
    lastName: guarantorLastName,
    dateOfBirth: guarantorDateOfBirth,
    email: guarantorEmail,
    phoneNumber: guarantorPhoneNumber,
    required: guarantorInfoRequired
  } = useGuarantorInformationFormStore(store => store);
  const {
    appointmentFacility,
    appointmentSlot,
    setAppointmentNumber,
    setInvalidAppointment,
    setPrevisitToken
  } = useAppointmentStore(store => store);
  const router = useRouter();
  const {
    locale
  } = router;
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const {
    completeActiveNode,
    lockSections
  } = useFormSectionStore(store => store);
  const {
    needsBasedAnswers,
    questionnaireId
  } = useNeedsBasedQuestionsStore(store => store);
  const {
    enableFraudCheck
  } = useAppStore(store => store);
  const {
    environment
  } = useAppContext();
  const openDialogWithError = useGlobalUXStore(store => store.openDialogWithError);
  const setIsLoading = useGlobalUXStore(store => store.setIsLoading);
  const [configLoaded, setConfigLoaded] = useState(false);
  const {
    isPending,
    mutate
  } = useScheduleAppointment();
  const sessionId = getSessionId();
  const {
    register
  } = useForm();
  const {
    ref: hiddenRef,
    ...hiddenProps
  } = register('ioBlackBox');
  const CONFIG_URL = environment === 'prod' ? APP_CONFIGURATIONS.services.prod.CONFIG_URL : APP_CONFIGURATIONS.services.nonprod.CONFIG_URL;
  const LOADER_URL = environment === 'prod' ? APP_CONFIGURATIONS.services.prod.LOADER_URL : APP_CONFIGURATIONS.services.nonprod.LOADER_URL;
  let mappedReasonForVisit;
  if (locale === 'es-US') {
    mappedReasonForVisit = REASONS_FOR_VISIT_ES.find(({
      value
    }) => value === reasonForVisit);
  } else {
    mappedReasonForVisit = REASONS_FOR_VISIT.find(({
      value
    }) => value === reasonForVisit);
  }
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const webFacilityDetails = useFacilityContext();
  const headerRef = useRef<HTMLElement>(null);
  const logger = getLogger('client');
  const sessionData = {
    userAgent: navigator.userAgent,
    lat: geoLocation?.latitude,
    long: geoLocation?.longitude,
    timestamp: new Date().getTime()
  };
  const submitTransunionFraudCheck = async () => {
    const currentPath = router.basePath + router.asPath;
    const ioBlackBox = document.getElementById('ioBlackBox') as HTMLInputElement;
    const payload = {
      emailAddress: email,
      ioBlackBox: ioBlackBox.value,
      sku: mappedReasonForVisit.value,
      pathUrl: currentPath,
      tenantId: `${appointmentFacility?.facilityCode}, ${appointmentFacility?.title}`,
      phoneNumber: phonenumber.replaceAll(/[^\d]/g, ''),
      sessionId: sessionId
    };
    try {
      await fetch(`${PREFIX}/check/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (error) {
      console.error('API call failed:', error);
    }
  };
  useImperativeHandle(ref, () => ({
    handleSubmit() {
      if (enableFraudCheck) {
        submitTransunionFraudCheck();
      }
      mutate(createAppointmentBody, {
        onSuccess: data => {
          setPrevisitToken(data.tempLoginToken);
          setAppointmentNumber(data.appointmentNumber ?? '');
          if (data.accounts) {
            setAccountNumber(data.accounts[0]?.accountNumber ?? '');
          }
          completeActiveNode();
          lockSections();
          userEvent({
            event_type: EventReviewAppointmentValueEventTypeEnum.AppointmentReview,
            event_data: {
              facility_code: appointmentFacility?.facilityCode ?? '',
              first_name: firstName,
              last_name: lastName,
              date_of_birth: new Intl.DateTimeFormat('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              }).format(dateOfBirth ?? undefined),
              email_address: email,
              mobile_phone: resolvePhoneNumberFormat(phonenumber) ?? '',
              is_insured: !!hasInsurance,
              visit_reason: reasonForVisit,
              appointment_start: appointmentSlot?.time ?? '',
              appointment_end: appointmentSlot?.time ?? ''
            }
          });
          setIsLoading(false);
          logger.info({
            message: 'APPOINTMENT_CREATED',
            ...sessionData
          });
        },
        onError: error => {
          console.error(error);
          openDialogWithError(error.message || t('unspecifiedError'));
          setIsLoading(false);
          logger.info({
            message: 'APPOINTMENT_ERROR',
            ...sessionData
          });
        }
      });
    }
  }));
  const appointmentInformation: AppointmentInformation[] = [{
    ...GenerateReviewTile('patientInfo', mottoFlow || denturesFlow),
    content: [`${firstName} ${lastName}`, new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(dateOfBirth ?? undefined), email, resolvePhoneNumberFormat(phonenumber)]
  }, {
    ...GenerateReviewTile('officeDetails', mottoFlow || denturesFlow),
    content: [appointmentFacility?.title, appointmentFacility?.address?.line1, `${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state} ${appointmentFacility?.zipCode}`, resolvePhoneNumberFormat(appointmentFacility?.phone ?? '')]
  }, {
    ...GenerateReviewTile('appointmentTime', mottoFlow || denturesFlow),
    content: [format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''), 'PPPP'), new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))]
  }, {
    ...GenerateReviewTile('visitReason', mottoFlow || denturesFlow, mappedReasonForVisit?.icon),
    content: [mappedReasonForVisit?.label]
  }];
  const appointmentInformationES: AppointmentInformation[] = [{
    ...generateReviewTileES('patientInfo', mottoFlow || denturesFlow),
    content: [`${firstName} ${lastName}`, new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(dateOfBirth ?? undefined), email, resolvePhoneNumberFormat(phonenumber)]
  }, {
    ...generateReviewTileES('officeDetails', mottoFlow || denturesFlow),
    content: [appointmentFacility?.title, appointmentFacility?.address?.line1, `${appointmentFacility?.address?.city}, ${appointmentFacility?.address?.state} ${appointmentFacility?.zipCode}`, resolvePhoneNumberFormat(appointmentFacility?.phone ?? '')]
  }, {
    ...generateReviewTileES('appointmentTime', mottoFlow || denturesFlow),
    content: [new Intl.DateTimeFormat(locale, {
      dateStyle: 'full'
    }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time))), new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit'
    }).format(new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? ''))]
  }, {
    ...generateReviewTileES('visitReason', mottoFlow || denturesFlow, mappedReasonForVisit?.icon),
    content: [mappedReasonForVisit?.label]
  }];
  const createAppointmentBody = {
    patient: {
      firstName,
      lastName,
      email,
      dateOfBirth: format(dateOfBirth as Date, 'yyyy-MM-dd'),
      phoneNumber: phonenumber.replaceAll(/[^\d]/g, ''),
      phoneType: PhoneType.Mobile,
      digiact
    },
    questionnaireAnsweredQuestions: needsBasedAnswers,
    ...(questionnaireId.length && {
      questionnaireId: questionnaireId
    }),
    insuranceFlag: hasInsurance ?? undefined,
    ...(guarantorInfoRequired && {
      guardian: {
        firstName: guarantorFirstName,
        lastName: guarantorLastName,
        email: guarantorEmail,
        dateOfBirth: format(guarantorDateOfBirth as Date, 'yyyy-MM-dd'),
        phoneNumber: guarantorPhoneNumber.replaceAll(/[^\d]/g, ''),
        phoneType: PhoneType.Mobile
      }
    }),
    application: Application.AspenDental,
    slotId: appointmentSlot?.slotId
  };
  const validateUserFields = async () => {
    try {
      const validateAccountResponse = await validateAccount({
        firstName,
        lastName,
        phoneNumber: phonenumber,
        dateOfBirth,
        facilityCode: appointmentFacility?.facilityCode ?? '',
        digitalVisitReason: reasonForVisit
      });
      const {
        newPatient,
        hasScheduledAppointment,
        digiact,
        childPatient,
        canSchedule
      } = validateAccountResponse;

      // save validation response values to store
      setNewPatient(newPatient || false);
      setHasScheduledAppointment(hasScheduledAppointment || false);
      setDigiact(digiact || 0);
      setChildPatient(childPatient || false);
      setInvalidAppointment(false);
      if (!canSchedule) {
        const existingAccountModalPayload = {
          event: 'modal',
          modal_location: 'scheduler',
          modal_purpose: 'existing account',
          modal_prompt: 'it looks like you already have an account with us',
          patient_type: 'existing patient',
          website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
          website_selected_facility_name: webFacilityDetails?.officeInfo?.name
        };
        openDialogWithError(t('alreadyScheduled'), {
          onDismiss: () => dataLayer({
            ...existingAccountModalPayload,
            modal_interaction: 'dismiss'
          })
        });
        setInvalidAppointment(true);
        dataLayer({
          ...existingAccountModalPayload,
          modal_interaction: 'not selected'
        });
      }
    } catch (e) {
      openDialogWithError(t('unspecifiedError'));
      console.error(e);
      setInvalidAppointment(true);
    }
  };

  // setting the session storage key to false for the patient information step to prevent multiple data layer events
  sessionStorageHelper.setKey(DataLayerStepNames.review, 'false');
  useEffect(() => {
    //this method is added to prevent multiple data layer events from being fired in mobile view
    //pass the step name as first parameter
    fireDataLayerEvent(DataLayerStepNames.review, {
      language_preference: locale,
      brand: mottoFlow ? 'mottoaligners' : 'aspendental',
      lead_score: appointmentSlot?.patientValue,
      lead_tier_national: appointmentSlot?.nationalPatientTier,
      lead_tier_office: appointmentSlot?.patientTier,
      step_name: DataLayerStepNames.review,
      reason_for_visit: reasonForVisit,
      reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
      dental_insurance: String(!!hasInsurance),
      ...(webFacilityDetails?.officeInfo?.code && {
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code
      }),
      ...(webFacilityDetails?.officeInfo?.name && {
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      }),
      ...(appointmentFacility?.facilityCode && {
        appointment_facility_id: appointmentFacility?.facilityCode
      }),
      ...(appointmentFacility?.title && {
        appointment_facility_name: appointmentFacility?.title
      })
    });
  }, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, appointmentFacility?.facilityCode, appointmentFacility?.title, hasInsurance, mottoFlow, appointmentSlot]);
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.confirmation,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });
  useEffect(() => {
    validateUserFields();
    if (headerRef?.current) {
      headerRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    isPending ? setIsLoading(true) : setIsLoading(false);
  }, [isPending]);
  return <>
			{enableFraudCheck && <>
					<Script src={CONFIG_URL} onLoad={() => {
        setConfigLoaded(true);
      }} onError={e => console.error('Config script failed to load:', e)} />
					{configLoaded && <Script src={LOADER_URL} onError={e => console.error('Loader script failed to load:', e)} />}
				</>}
			<Typography variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'} color="text.secondary" tabIndex={0} ref={headerRef}>
				{t('clickConfirm')}
			</Typography>

			{locale === 'en-US' && <Grid container spacing={2} mt={1}>
					{appointmentInformation.map(({
        title,
        content,
        icon,
        editSectionIndex
      }) => {
        return <Grid item key={title} xs={12} sm={6}>
								<AppointmentCard title={title} content={content} icon={icon} editSectionIndex={editSectionIndex} />
							</Grid>;
      })}
				</Grid>}

			{locale === 'es-US' && <Grid container spacing={2} mt={1}>
					{appointmentInformationES.map(({
        title,
        content,
        icon,
        editSectionIndex
      }) => {
        return <Grid item key={title} xs={12} sm={6}>
								<AppointmentCard title={title} content={content} icon={icon} editSectionIndex={editSectionIndex} />
							</Grid>;
      })}
				</Grid>}
			<input type="hidden" id="ioBlackBox" {...hiddenProps} ref={hiddenRef} />
		</>;
});
export default AppointmentReview;